import axios from "axios";
import { useState, useContext, useEffect, useCallback } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  Table,
  Typography,
  Row,
  Col,
  Select,
  Button,
  Tag,
  Input,
  Badge,
  Spin,
  Space
} from "antd";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  SearchOutlined
} from "@ant-design/icons";
import ProbationReviewModal from "./ProbationReviewModal";

const { Title } = Typography;
const { Option } = Select;

const STATUS_TABS = [
  { key: "Pending", label: "Pending Approvals", color: "#faad14", icon: <ClockCircleOutlined /> },
  { key: "Confirm", label: "Confirmed",         color: "#52c41a", icon: <CheckCircleOutlined /> },
  { key: "Extend",  label: "Extended",          color: "#0e497b", icon: <ReloadOutlined /> },
  { key: "Separated", label: "Separated",       color: "#ff4d4f", icon: <CloseCircleOutlined /> },
];

const TAG_COLOR = {
  Pending: "warning",
  Confirm: "success",
  Extend: "processing",
  Separated: "error",
};

const TAG_LABEL = {
  Pending: "Pending",
  Confirm: "Confirmed",
  Extend: "Extended",
  Separated: "Separated",
};

export default function ProbationReview() {
  const { backUrl, authData } = useContext(AuthContext);

  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("Pending");

  // Counts shown on each tab badge
  const [tabCounts, setTabCounts] = useState({ Pending: 0, Confirm: 0, Extend: 0, Separated: 0 });

  const [searchName, setSearchName] = useState("");
  const [searchEmpCode, setSearchEmpCode] = useState("");
  
  const [pagination, setPagination] = useState({ 
    current: 1, 
    pageSize: 50 
  });

  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [Organisations, setOrganisations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [organisationFilter, setOrganisationFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [designationFilter, setDesignationFilter] = useState("");

  const headers = { Authorization: `Bearer ${authData?.access}` };

  const buildUrl = useCallback(
    (statusKey) => {
      const params = new URLSearchParams();
      params.append("view_mode", "hr");

      if (statusKey) {
        params.append("overall_status", statusKey);
      }
      
      if (statusKey === "Pending") {
        params.append("is_ready", "true");
      }

      const qs = params.toString();
      return `${backUrl}/api/probation/probation-reviews/${qs ? "?" + qs : ""}`;
    },
    [backUrl]
  );

  const fetchData = useCallback(
    async (tab) => {
      const tabKey = tab ?? activeTab;
      setLoading(true);
      try {
        const res = await axios.get(buildUrl(tabKey), { headers });
        const data = res.data.results || [];
        setEmployees(data);
      } catch (err) {
        console.error(err);
        toast.error("Failed to fetch probation reviews.");
      } finally {
        setLoading(false);
      }
    },
    [activeTab, buildUrl]
  );

  const fetchCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${backUrl}/api/probation/probation-reviews/counts/?view_mode=hr`, { headers });
      setTabCounts(res.data);
    } catch (_) {
      console.error("Failed to fetch badge counts");
    }
  }, [backUrl]);

  const fetchOrganisation = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/emp/organisations/`, { headers });
      setOrganisations(res.data.results || []);
    } catch (_) { }
  };

  const fetchDepartments = async (orgName) => {
    if (!orgName) return;
    const org = Organisations.find(o => o.name === orgName);
    if (!org) return;
    const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${org.id}`, { headers });
    setDepartments(res.data.results || []);
  };

  const fetchDesignations = async (deptName) => {
    if (!deptName) return;
    const dept = departments.find(d => d.name === deptName);
    if (!dept) return;
    const res = await axios.get(`${backUrl}/api/emp/designations/?department=${dept.id}`, { headers });
    setDesignations(res.data.results || []);
  };

  useEffect(() => {
    fetchOrganisation();
    fetchData("Pending");
    fetchCounts();
  }, []);

  useEffect(() => {
    fetchData(activeTab);
  }, [activeTab]);

  useEffect(() => {
    let filtered = [...employees];

    if (organisationFilter) filtered = filtered.filter((i) => i.employee?.organisation_name === organisationFilter);
    if (departmentFilter) filtered = filtered.filter((i) => i.employee?.department_name === departmentFilter);
    if (designationFilter) filtered = filtered.filter((i) => i.employee?.designation_name === designationFilter);

    if (searchEmpCode) {
      filtered = filtered.filter((i) =>
        i.employee?.emp_code?.toLowerCase().includes(searchEmpCode.toLowerCase())
      );
    }
    if (searchName) {
      filtered = filtered.filter((i) =>
        `${i.employee?.first_name || ""} ${i.employee?.last_name || ""}`
          .toLowerCase()
          .includes(searchName.toLowerCase())
      );
    }

    setFilteredEmployees(filtered);
  }, [organisationFilter, departmentFilter, designationFilter, searchEmpCode, searchName, employees]);

  const handleClearFilters = () => {
    setSearchName(""); setSearchEmpCode("");
    setOrganisationFilter(""); setDepartmentFilter(""); setDesignationFilter("");
    setDepartments([]); setDesignations([]);
  };

  const getCount = (key) => {
    if (key === "Pending") return tabCounts.Ready ?? tabCounts.Pending ?? 0;
    const countKey = key === "Confirm" ? "Confirmed" : (key === "Extend" ? "Extended" : key);
    return tabCounts[countKey] ?? 0;
  };

  const columns = [
    {
      title: "Emp Code",
      dataIndex: ["employee", "emp_code"],
      key: "emp_code",
      width: 120,
      fixed: "left",
      render: (text, record) => (
        <a
          onClick={() => { setSelectedEmployee(record); setShowModal(true); }}
          style={{ cursor: "pointer", fontWeight: 600, color: "#0e497b" }}
        >
          {text}
        </a>
      ),
    },
    {
      title: "Employee Name",
      key: "employee_name",
      width: 200,
      render: (_, r) => `${r.employee?.first_name || ""} ${r.employee?.last_name || ""}`.trim() || "—",
    },
    {
      title: "Date of Joining",
      dataIndex: ["employee", "date_of_joining"],
      key: "doj",
      render: (t) => (t ? new Date(t).toLocaleDateString("en-GB") : "—"),
    },
    {
      title: "Probation End Date",
      dataIndex: "final_probation_end_date",
      key: "prob_end",
      render: (t) => (t ? new Date(t).toLocaleDateString("en-GB") : "—"),
    },
    {
      title: "Status",
      dataIndex: "overall_status",
      key: "status",
      render: (s) => <Tag color={TAG_COLOR[s] || "default"}>{TAG_LABEL[s] || s}</Tag>,
    },
    {
      title: "Organisation",
      dataIndex: ["employee", "organisation_name"],
      key: "organisation",
    },
    {
      title: "Department",
      dataIndex: ["employee", "department_name"],
      key: "department",
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Button
          size="small"
          type="primary"
          icon={<EyeOutlined />}
          style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", borderRadius: 4 }}
          onClick={() => { setSelectedEmployee(record); setShowModal(true); }}
        >
          Review
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      <center>
        <Title level={5} style={{ marginBottom: 16, color: "#0e497b", textTransform: 'uppercase' }}>
          Probation Reviews
        </Title>
      </center>

      {/* Tabs Styled as Buttons - Following TeamCompoff Pattern */}
      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        {STATUS_TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <Button
              key={tab.key}
              type={isActive ? "primary" : "default"}
              onClick={() => setActiveTab(tab.key)}
              style={{
                marginRight: "10px",
                backgroundColor: isActive ? "#0e497b" : undefined,
                borderColor: isActive ? "#0e497b" : undefined,
                color: isActive ? "#fff" : undefined,
              }}
            >
              {tab.label} ({getCount(tab.key)})
            </Button>
          );
        })}
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => { fetchData(activeTab); fetchCounts(); }}
        >
          Refresh
        </Button>
      </div>

      {/* Filters Container - Following Pattern */}
      <div className="filters-container" style={{ background: "#fff", padding: "16px", borderRadius: "8px", marginBottom: "20px", border: "1px solid #f0f0f0" }}>
        <Row gutter={[12, 12]} justify="center">
          <Col xs={24} sm={12} md={6} lg={4}>
            <Input
              placeholder="Emp Code"
              value={searchEmpCode}
              onChange={(e) => setSearchEmpCode(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Input
              placeholder="Employee Name"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Select
              placeholder="Select Organisation"
              style={{ width: "100%" }}
              value={organisationFilter || undefined}
              onChange={(val) => { setOrganisationFilter(val); setDepartmentFilter(""); setDesignationFilter(""); fetchDepartments(val); }}
              allowClear
            >
              {Organisations.map((o) => <Option key={o.id} value={o.name}>{o.name}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Select
              showSearch
              placeholder="Select Department"
              style={{ width: "100%" }}
              value={departmentFilter || undefined}
              onChange={(val) => { setDepartmentFilter(val); setDesignationFilter(""); fetchDesignations(val); }}
              allowClear
              disabled={!organisationFilter}
              filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}
            >
              {departments.map((d) => <Option key={d.id} value={d.name}>{d.name}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Select
              showSearch
              placeholder="Select Designation"
              style={{ width: "100%" }}
              value={designationFilter || undefined}
              onChange={(val) => setDesignationFilter(val)}
              allowClear
              disabled={!departmentFilter}
              filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}
            >
              {designations.map((d) => <Option key={d.id} value={d.name}>{d.name}</Option>)}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Button block type="primary" style={{ backgroundColor: "#0e497b", borderColor: "#0e497b" }} onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Col>
        </Row>
      </div>

      <div className="table-responsive">
        <Table
          columns={columns}
          dataSource={filteredEmployees}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: filteredEmployees.length,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            onChange: (p, s) => setPagination({ current: p, pageSize: s }),
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
          }}
          rowKey={(r) => r.id}
          bordered
          size="middle"
          scroll={{ x: "max-content" }}
          components={{
            header: {
              cell: (props) => (
                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                  {props.children}
                </th>
              ),
            },
          }}
        />
      </div>

      {showModal && selectedEmployee && (
        <ProbationReviewModal
          visible={showModal}
          employee={selectedEmployee}
          onClose={() => {
            setShowModal(false);
            fetchData(activeTab);
            fetchCounts();
          }}
        />
      )}
    </div>
  );
}